import React from 'react';

/**
 * Catches uncaught React render errors so a single broken page doesn't unmount
 * the entire app (which is why https://homecontrol.in/meals went fully white
 * — no boundary existed). Designed to wrap the page slot inside `Layout` so
 * the sidebar + topbar remain visible.
 *
 * Props:
 *   onReset? — optional callback invoked when the user clicks "Try again"
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console + (best-effort) to backend for triage. We never let the
    // log call itself throw — that would put us back into a render loop.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/health/client-error';
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: String(error?.message || error).slice(0, 500),
          stack: String(error?.stack || '').slice(0, 2000),
          component_stack: String(info?.componentStack || '').slice(0, 2000),
          path: typeof window !== 'undefined' ? window.location.pathname : '',
          ua: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : '',
          ts: new Date().toISOString(),
        }),
      }).catch(() => {});
    } catch (_) { /* never let logging crash */ }
    this.setState({ info });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null });
    if (typeof this.props.onReset === 'function') this.props.onReset();
  };

  handleGoHome = () => {
    if (typeof window !== 'undefined') window.location.href = '/dashboard';
  };

  handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error || 'Unknown error');
    return (
      <div data-testid="error-boundary-fallback"
        className="rounded-2xl p-6 sm:p-8"
        style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)' }}>
        <div className="flex items-start gap-3 mb-4">
          <span className="text-2xl flex-shrink-0">⚠️</span>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-red-300 mb-1">This page hit an error</h2>
            <p className="text-sm text-neutral-400">
              Something on this page crashed. The rest of the app still works — try the buttons below or use the sidebar to navigate.
            </p>
          </div>
        </div>

        <details className="rounded-xl p-3 mb-4" style={{ background: 'rgba(0,0,0,0.30)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <summary className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 cursor-pointer">
            Technical details
          </summary>
          <p className="text-[11px] text-neutral-300 mt-2 font-mono break-words" data-testid="error-boundary-message">
            {msg}
          </p>
          {this.state.info?.componentStack && (
            <pre className="text-[10px] text-neutral-500 mt-2 overflow-x-auto whitespace-pre-wrap">
              {String(this.state.info.componentStack).slice(0, 1500)}
            </pre>
          )}
        </details>

        <div className="flex flex-wrap gap-2">
          <button onClick={this.handleReset} data-testid="error-boundary-retry"
            className="px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-xs font-bold hover:bg-[#E85D2A]">
            Try again
          </button>
          <button onClick={this.handleReload} data-testid="error-boundary-reload"
            className="px-4 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-bold hover:bg-white/10 border border-white/10">
            Reload page
          </button>
          <button onClick={this.handleGoHome} data-testid="error-boundary-home"
            className="px-4 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-bold hover:bg-white/10 border border-white/10">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
}
